/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, lazy, useEffect } from "react"
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom"

import { SessionErrorPage } from "components/Error/SessionErrorPage"
import { LocaleProvider } from "components/Locale/LocaleProvider"
import LazyLoadWrapper from "components/LazyLoadWrapper"
import { isDevBuildOrBranchPreview, isPrd } from "constants/domain"
import {
  HOME,
  LOGIN,
  LOGIN_SUCCESS,
  LOGOUT,
  MART_ECOMM_MIGRATION_PREVIEW,
} from "constants/routes"
import { useAppBottomNavVisibility } from "hooks/useAppBottomNavVisibility"
import { Home } from "pages/home"
import { Login } from "pages/login"
import { LoginSuccess } from "pages/login/LoginSuccess"
import { devLogout, isAuthenticated, logout } from "utils/authentication"
import { sessionInfo } from "utils/session"
import { emitSessionError, SessionErrorReason } from "utils/sessionError"
import { storageHelper } from "utils/storage"
import { isWebview } from "utils/webview"

const MigrationPreviewPage = lazy(() =>
  import("ecommerce/pages/migration-preview/MigrationPreviewPage"),
)

const LogoutRoute: FC = () => {
  useEffect(() => {
    devLogout()
    window.location.replace(LOGIN)
  }, [])

  return null
}

/**
 * Which of the gate's requirements failed, so the report names an owner rather
 * than lumping three causes under one label.
 *
 * Mirrors `isAuthenticated` (`utils/authentication`) rather than re-deriving
 * it: that function short-circuits to true on a readable profile under cookie
 * auth, otherwise falls back to the token, so a rejection WITH a token stored
 * means the token itself was bad, and a rejection WITHOUT one means nothing
 * usable was handed over at all. Keep this in step if that order changes.
 */
const gateFailureReason = (): SessionErrorReason => {
  if (isAuthenticated()) return "missingCountry"
  return storageHelper.getToken() ? "invalidToken" : "missingProfile"
}

/**
 * Keeps the app's bottom nav bar in sync with what is on screen. Renders
 * nothing; it exists only to give the hook a mount point.
 *
 * Deliberately OUTSIDE the `Switch` and ABOVE the auth gate: the hook has to
 * stay mounted for every route of every page load, including the ones where
 * `AuthenticatedRoutes` renders nothing. Unmounting it strands the bar at its
 * last asserted value. Same placement, and the same reason, as
 * `SessionErrorPage`.
 *
 * Rendered after the `Switch` for a much smaller reason, worth keeping but not
 * to be confused with the above: mount effects run in tree order and the gate
 * raises its session error from an effect, so mounting first would assert the
 * route's own answer — "hide", on a full-page product screen — one commit
 * before the hook's own subscription corrects it. A single frame of
 * wrongly-hidden bar, not a stranded one.
 */
const AppBottomNavSync: FC = () => {
  useAppBottomNavVisibility()
  return null
}

const AuthenticatedRoutes: FC = () => {
  const authentication = isAuthenticated()
  const userCountry = storageHelper.getUserCountry()
  const hasNoSession = !authentication || userCountry == null
  // Inside the native app webview the portal has no login of its own: the app
  // owns the session and injects the token. If it hands over an incomplete one,
  // sending the merchant to the portal login page is a dead end — raise a
  // session error instead and let `SessionErrorPage` (mounted above this gate in
  // `RootRouter`) offer them a retry. Same stance as `http401UnauthorizedHandler`
  // in `utils/network`, including leaving `logout()` alone: it clears the store
  // search history, which the retry has no use for and the merchant would rather
  // keep once the session comes back.
  const isRecoverableInApp = hasNoSession && isWebview()

  // Raised from an effect, not from render: on a re-render that loses the
  // session (token expiry + a route change) `SessionErrorPage` is already
  // mounted and subscribed, and notifying it mid-render would set state on
  // another component while this one renders.
  useEffect(() => {
    if (isRecoverableInApp) {
      emitSessionError(gateFailureReason())
    }
  }, [isRecoverableInApp])

  if (hasNoSession) {
    if (isRecoverableInApp) return null
    logout()
    if (
      window.location.pathname !== "/" &&
      window.location.pathname !== LOGOUT
    ) {
      window.location.href = LOGOUT
    }
    return null
  }
  sessionInfo.setUserCurrentCountryCode(userCountry)
  // `hasNoSession` already covers `!authentication || userCountry == null`, so
  // only the logout route is left to reject here.
  if (window.location.pathname === LOGOUT) return null

  return <Route component={Home} />
}

export const RootRouter = () => {
  const isLoginRouteEnabled = isDevBuildOrBranchPreview()

  return (
    <Router>
      <Switch>
        {isLoginRouteEnabled && <Redirect exact from={HOME} to={LOGIN} />}
        {isLoginRouteEnabled && <Route exact path={LOGIN} component={Login} />}
        {isLoginRouteEnabled && (
          <Route exact path={LOGOUT} component={LogoutRoute} />
        )}
        {isLoginRouteEnabled && (
          <Route exact path={LOGIN_SUCCESS} component={LoginSuccess} />
        )}
        {!isPrd() && (
          <Route exact path={MART_ECOMM_MIGRATION_PREVIEW}>
            <LazyLoadWrapper>
              <MigrationPreviewPage />
            </LazyLoadWrapper>
          </Route>
        )}
        <Route component={AuthenticatedRoutes} />
      </Switch>
      {/*
        Outside the Switch and above the auth gate: the webview session error is
        raised exactly when `AuthenticatedRoutes` renders nothing, so a screen
        mounted inside it could never show. Renders null until a session error
        is raised. `LocaleProvider` is what keeps its copy translated up here —
        `HomeContainer`'s provider is behind the gate.
      */}
      <LocaleProvider>
        <SessionErrorPage />
      </LocaleProvider>
      <AppBottomNavSync />
    </Router>
  )
}
